import { useCallback, useEffect, useRef, useState } from 'react';

import {
  COACH_CANCEL_APPOINTMENT_CONTRACT,
  EXACT_APPOINTMENT_CONTRACT,
  NATIVE_COACH_RESCHEDULE_CONTRACT,
  PREVIOUS_EXACT_APPOINTMENT_CONTRACT,
  SINGLE_SESSION_APPOINTMENT_CONTRACT,
} from './browserCapability';
import {
  notifyReceiptOwner,
  subscribeReceiptOwner,
} from './receiptOwnerChannel';

import type {
  AppointmentBrowserCapability,
  AppointmentBrowserContractMarker,
} from './browserCapability';

export interface AppointmentPreimage {
  currentSchedule?: { startAt: string };
  id: string;
  seriesId: string | null;
  seriesMutationVersion?: number;
  version: number;
}

interface DisplayedAppointmentPreimage {
  id?: string | null;
  seriesId?: string | null;
  seriesMutationVersion?: number | null;
  version?: number | null;
}

interface RejectedCommand {
  bytes: string;
  idempotencyKey: string;
  operationId: string;
  submittedAt?: string;
}

type SubmittingCommand = Omit<RejectedCommand, 'operationId'>;
type StoredSubmittingCommand = SubmittingCommand & {
  capabilityMarker: AppointmentBrowserContractMarker;
  submittedAt?: string;
};

interface StoredLifecycleRecord {
  bytes: string;
  capabilityMarker: AppointmentBrowserContractMarker;
  idempotencyKey: string;
  operationId: string | null;
  ownerKey: string;
  retryAfterSeconds: number | null;
  status:
    | 'confirmed'
    | 'submitting'
    | 'pending'
    | 'needs_reconciliation'
    | 'reconciliation_required'
    | 'rejected';
  submittedAt?: string;
  terminalSummary?: {
    affectedCount: number;
    windowEnd: string | null;
    windowStart: string | null;
  };
  transport?: AppointmentLifecycleTransport;
  version: 2 | 3;
}

export type AppointmentLifecycleTransport = 'moments' | 'rails';

export type StoredAppointmentLifecycleReceipt = Pick<
  StoredLifecycleRecord,
  'bytes' | 'ownerKey' | 'status'
>;

export type StoredAppointmentLifecycleRecovery = Pick<
  StoredLifecycleRecord,
  | 'bytes'
  | 'operationId'
  | 'ownerKey'
  | 'retryAfterSeconds'
  | 'status'
  | 'transport'
>;

const STORAGE_PREFIX = 'appointment-lifecycle:';
const confirmedClaims = new Map<string, symbol>();

const storageKey = (ownerKey: string) =>
  `${STORAGE_PREFIX}${encodeURIComponent(ownerKey)}`;

const legacyStorageKey = (ownerKey: string) =>
  `${STORAGE_PREFIX}${encodeURIComponent(ownerKey.split(':')[0] ?? ownerKey)}`;

const removeStoredLifecycleRecord = (ownerKey: string, notify = true) => {
  try {
    window.sessionStorage.removeItem(storageKey(ownerKey));
  } catch {
    return false;
  }
  if (notify) notifyReceiptOwner(ownerKey);
  return true;
};

const sameLifecycleRecord = (
  left: StoredLifecycleRecord | null,
  right: StoredLifecycleRecord | null,
) =>
  left === right ||
  (left !== null &&
    right !== null &&
    left.bytes === right.bytes &&
    left.capabilityMarker === right.capabilityMarker &&
    left.idempotencyKey === right.idempotencyKey &&
    left.operationId === right.operationId &&
    left.ownerKey === right.ownerKey &&
    left.retryAfterSeconds === right.retryAfterSeconds &&
    left.submittedAt === right.submittedAt &&
    left.transport === right.transport &&
    JSON.stringify(left.terminalSummary) ===
      JSON.stringify(right.terminalSummary) &&
    left.status === right.status);

const parseStoredLifecycleRecord = (raw: string, ownerKey: string) => {
  if (raw.length > 8192) return null;
  const value = JSON.parse(raw) as Partial<StoredLifecycleRecord>;
  const { transport } = value as { transport?: unknown };
  const submittedAtMillis =
    typeof value.submittedAt === 'string'
      ? Date.parse(value.submittedAt)
      : Number.NaN;
  const validSubmittedAt =
    value.submittedAt === undefined ||
    (typeof value.submittedAt === 'string' &&
      value.submittedAt.length <= 32 &&
      Number.isFinite(submittedAtMillis) &&
      new Date(submittedAtMillis).toISOString() === value.submittedAt);
  const terminalSummary = value.terminalSummary as
    Partial<NonNullable<StoredLifecycleRecord['terminalSummary']>> | undefined;
  const validTerminalSummary =
    terminalSummary === undefined ||
    (value.version === 3 &&
      value.status === 'confirmed' &&
      typeof terminalSummary === 'object' &&
      Number.isInteger(terminalSummary.affectedCount) &&
      (terminalSummary.affectedCount ?? 0) > 0 &&
      [terminalSummary.windowStart, terminalSummary.windowEnd].every(
        (timestamp) =>
          timestamp === null ||
          (typeof timestamp === 'string' &&
            timestamp.length <= 32 &&
            Number.isFinite(Date.parse(timestamp))),
      ));
  if (
    (value.version !== 2 && value.version !== 3) ||
    value.ownerKey !== ownerKey ||
    (value.capabilityMarker !== EXACT_APPOINTMENT_CONTRACT &&
      value.capabilityMarker !== PREVIOUS_EXACT_APPOINTMENT_CONTRACT &&
      value.capabilityMarker !== SINGLE_SESSION_APPOINTMENT_CONTRACT &&
      value.capabilityMarker !== COACH_CANCEL_APPOINTMENT_CONTRACT &&
      value.capabilityMarker !== NATIVE_COACH_RESCHEDULE_CONTRACT) ||
    (value.version === 2 &&
      value.capabilityMarker !== EXACT_APPOINTMENT_CONTRACT &&
      value.capabilityMarker !== PREVIOUS_EXACT_APPOINTMENT_CONTRACT &&
      value.capabilityMarker !== COACH_CANCEL_APPOINTMENT_CONTRACT) ||
    (value.status !== 'confirmed' &&
      value.status !== 'submitting' &&
      value.status !== 'pending' &&
      value.status !== 'needs_reconciliation' &&
      value.status !== 'reconciliation_required' &&
      value.status !== 'rejected') ||
    typeof value.bytes !== 'string' ||
    value.bytes.length > 4096 ||
    typeof value.idempotencyKey !== 'string' ||
    value.idempotencyKey.length > 256 ||
    !validSubmittedAt ||
    !validTerminalSummary ||
    ((value.status === 'pending' ||
      value.status === 'needs_reconciliation' ||
      value.status === 'reconciliation_required' ||
      value.status === 'rejected') &&
      (typeof value.operationId !== 'string' ||
        value.operationId.length > 256)) ||
    ((value.status === 'confirmed' || value.status === 'submitting') &&
      value.operationId !== null) ||
    (value.retryAfterSeconds !== null &&
      (typeof value.retryAfterSeconds !== 'number' ||
        !Number.isFinite(value.retryAfterSeconds) ||
        value.retryAfterSeconds < 0 ||
        value.retryAfterSeconds > 3600)) ||
    (transport !== undefined &&
      transport !== 'moments' &&
      transport !== 'rails')
  ) {
    return null;
  }
  return value as StoredLifecycleRecord;
};

const storedLifecycleRecord = (
  ownerKey: string | undefined,
  legacyTransport?: AppointmentLifecycleTransport,
): StoredLifecycleRecord | null => {
  if (!ownerKey) return null;
  try {
    const exactKey = storageKey(ownerKey);
    const exactRaw = window.sessionStorage.getItem(exactKey);
    if (exactRaw) {
      const exactRecord = parseStoredLifecycleRecord(exactRaw, ownerKey);
      if (exactRecord) {
        if (!exactRecord.transport && legacyTransport) {
          const migrated = { ...exactRecord, transport: legacyTransport };
          window.sessionStorage.setItem(exactKey, JSON.stringify(migrated));
          return migrated;
        }
        return exactRecord;
      }
    }

    const legacyKey = legacyStorageKey(ownerKey);
    if (legacyKey === exactKey) return null;
    const legacyRaw = window.sessionStorage.getItem(legacyKey);
    if (!legacyRaw) return null;
    const legacyRecord = parseStoredLifecycleRecord(legacyRaw, ownerKey);
    if (!legacyRecord) return null;
    let migrated = legacyRecord;
    if (!legacyRecord.transport && legacyTransport) {
      migrated = { ...legacyRecord, transport: legacyTransport };
    }
    try {
      window.sessionStorage.setItem(exactKey, JSON.stringify(migrated));
      window.sessionStorage.removeItem(legacyKey);
    } catch {
      // Restoring remains safe even when best-effort migration is unavailable.
    }
    return migrated;
  } catch {
    return null;
  }
};

export const storedAppointmentLifecycleTransport = (
  ownerKey: string | undefined,
  legacyTransport: AppointmentLifecycleTransport,
): AppointmentLifecycleTransport | undefined =>
  storedLifecycleRecord(ownerKey, legacyTransport)?.transport;

export const storedAppointmentLifecycleRecovery = (
  ownerKey: string | undefined,
  legacyTransport: AppointmentLifecycleTransport,
): StoredAppointmentLifecycleRecovery | undefined =>
  storedLifecycleRecord(ownerKey, legacyTransport) ?? undefined;

export const storedAppointmentLifecycleReceipts = (
  operationFamily: string,
): StoredAppointmentLifecycleReceipt[] => {
  const receipts: StoredAppointmentLifecycleReceipt[] = [];
  try {
    Array.from({ length: window.sessionStorage.length }, (_, index) =>
      window.sessionStorage.key(index),
    ).forEach((key) => {
      if (!key?.startsWith(STORAGE_PREFIX)) return;
      const raw = window.sessionStorage.getItem(key);
      if (!raw) return;
      try {
        const { ownerKey } = JSON.parse(raw) as { ownerKey?: unknown };
        if (
          typeof ownerKey === 'string' &&
          ownerKey.startsWith(`${operationFamily}:`) &&
          key === storageKey(ownerKey)
        ) {
          const record = parseStoredLifecycleRecord(raw, ownerKey);
          if (record) receipts.push(record);
        }
      } catch {
        // Ignore unrelated or malformed browser storage.
      }
    });
  } catch {
    return [];
  }
  return receipts;
};

const appointmentPreimage = (value: unknown): AppointmentPreimage | null => {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Partial<AppointmentPreimage>;
  const { currentSchedule } = value as { currentSchedule?: unknown };
  if (
    typeof candidate.id !== 'string' ||
    !Number.isInteger(candidate.version) ||
    (candidate.version ?? 0) <= 0 ||
    (currentSchedule !== undefined &&
      (currentSchedule === null ||
        typeof currentSchedule !== 'object' ||
        typeof (currentSchedule as { startAt?: unknown }).startAt !== 'string'))
  ) {
    return null;
  }
  const validFence =
    (candidate.seriesId === null && candidate.seriesMutationVersion === 0) ||
    (typeof candidate.seriesId === 'string' &&
      candidate.seriesId.length > 0 &&
      Number.isInteger(candidate.seriesMutationVersion) &&
      (candidate.seriesMutationVersion ?? 0) > 0);
  return validFence ? (candidate as AppointmentPreimage) : null;
};

const preimageMatches = (
  left: AppointmentPreimage,
  right: DisplayedAppointmentPreimage,
) =>
  left.id === right.id &&
  left.version === right.version &&
  left.seriesId === right.seriesId &&
  left.seriesMutationVersion === right.seriesMutationVersion;

export const canonicalAppointmentCommand = (command: object) =>
  JSON.stringify(command);

export const appointmentUpgradeGuardSpent = (error: unknown) => {
  if (!error || typeof error !== 'object' || !('upgrade' in error))
    return false;
  const { upgrade } = error as { upgrade?: { state?: unknown } };
  return upgrade?.state === 'guard-spent';
};

export const appointmentSeriesCommandFields = (
  _capability: AppointmentBrowserCapability,
  appointment: AppointmentPreimage,
) => ({
  expectedSeriesMutationVersion:
    appointment.seriesId === null ? null : appointment.seriesMutationVersion!,
  seriesId: appointment.seriesId,
});

export const useAppointmentLifecycleGuard = ({
  allowReviewedPreimage = true,
  capability,
  displayed,
  ownerKey,
  protocolMarker,
  readAppointment,
  transport,
  legacyTransport,
}: {
  allowReviewedPreimage?: boolean;
  capability: AppointmentBrowserCapability;
  displayed: DisplayedAppointmentPreimage;
  ownerKey?: string;
  protocolMarker?: AppointmentBrowserContractMarker;
  readAppointment: () => Promise<unknown>;
  transport?: AppointmentLifecycleTransport;
  legacyTransport?: AppointmentLifecycleTransport;
}) => {
  const activeCapability = useRef(capability).current;
  const activeProtocolMarker = useRef(
    protocolMarker ?? capability.marker,
  ).current;
  const lifecycleRecordVersion: 3 = 3;
  const ownerIdentity = useRef({ generation: 0, ownerKey });
  if (ownerIdentity.current.ownerKey !== ownerKey) {
    ownerIdentity.current = {
      generation: ownerIdentity.current.generation + 1,
      ownerKey,
    };
  }
  const initialRecord = useRef(
    storedLifecycleRecord(ownerKey, legacyTransport),
  ).current;
  const activeTransport = transport ?? initialRecord?.transport;
  const [reviewed, setReviewed] = useState<AppointmentPreimage | null>(null);
  const [reviewUnavailable, setReviewUnavailable] = useState(false);
  const [upgradeRequired, setUpgradeRequired] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [confirmedReceipt, setConfirmedReceipt] =
    useState<StoredLifecycleRecord | null>(null);
  const [restoredSubmitting, setRestoredSubmitting] =
    useState<StoredSubmittingCommand | null>(() =>
      initialRecord?.status === 'submitting' ? initialRecord : null,
    );
  const [restoredPending, setRestoredPending] =
    useState<StoredLifecycleRecord | null>(() =>
      initialRecord?.status === 'pending' ? initialRecord : null,
    );
  const [restoredReconciliation, setRestoredReconciliation] =
    useState<StoredLifecycleRecord | null>(() =>
      initialRecord?.status === 'reconciliation_required'
        ? initialRecord
        : null,
    );
  const [rejected, setRejected] = useState<RejectedCommand | null>(() =>
    initialRecord?.status === 'rejected'
      ? (initialRecord as RejectedCommand)
      : null,
  );
  const [needsReconciliation, setNeedsReconciliation] =
    useState<StoredLifecycleRecord | null>(() =>
      initialRecord?.status === 'needs_reconciliation' ? initialRecord : null,
    );
  const readAppointmentRef = useRef(readAppointment);
  readAppointmentRef.current = readAppointment;
  const commandRef = useRef<SubmittingCommand | null>(initialRecord);
  const submittingRef = useRef<StoredSubmittingCommand | null>(
    restoredSubmitting,
  );
  const stateOwnerKey = useRef(ownerKey);
  const installedRecordRef = useRef(
    initialRecord?.status === 'confirmed' ? null : initialRecord,
  );
  const receiptClaimToken = useRef(Symbol('appointment-confirmed-receipt'));
  const subscribedRef = useRef(false);
  const suppressConfirmedSyncRef = useRef(false);

  const installRecord = useCallback((record: StoredLifecycleRecord | null) => {
    if (sameLifecycleRecord(installedRecordRef.current, record)) return;
    installedRecordRef.current = record;
    const confirmed = record?.status === 'confirmed' ? record : null;
    const activeRecord = confirmed ? null : record;
    setConfirmedReceipt(confirmed);
    setRestoredPending(
      activeRecord?.status === 'pending' ? activeRecord : null,
    );
    setRestoredReconciliation(
      activeRecord?.status === 'reconciliation_required' ? activeRecord : null,
    );
    const submitting =
      activeRecord?.status === 'submitting' ? activeRecord : null;
    submittingRef.current = submitting;
    setRestoredSubmitting(submitting);
    setRejected(
      activeRecord?.status === 'rejected'
        ? (activeRecord as RejectedCommand)
        : null,
    );
    setNeedsReconciliation(
      activeRecord?.status === 'needs_reconciliation' ? activeRecord : null,
    );
    commandRef.current = activeRecord;
  }, []);

  useEffect(() => {
    if (!ownerKey) return undefined;
    const syncRecord = () => {
      const record = storedLifecycleRecord(ownerKey, legacyTransport);
      if (record?.status === 'confirmed') {
        if (suppressConfirmedSyncRef.current) return;
        const claimedBy = confirmedClaims.get(ownerKey);
        if (claimedBy && claimedBy !== receiptClaimToken.current) {
          installRecord(null);
          return;
        }
        confirmedClaims.set(ownerKey, receiptClaimToken.current);
      }
      installRecord(record);
    };
    const unsubscribe = subscribeReceiptOwner(ownerKey, syncRecord);
    subscribedRef.current = true;
    syncRecord();
    return () => {
      subscribedRef.current = false;
      unsubscribe();
      if (confirmedClaims.get(ownerKey) === receiptClaimToken.current) {
        confirmedClaims.delete(ownerKey);
        notifyReceiptOwner(ownerKey);
      }
    };
  }, [installRecord, legacyTransport, ownerKey]);

  useEffect(() => {
    if (stateOwnerKey.current === ownerKey) return;

    stateOwnerKey.current = ownerKey;
    const record = storedLifecycleRecord(ownerKey, legacyTransport);
    setReviewed(null);
    setReviewUnavailable(false);
    setUpgradeRequired(false);
    setIsReading(false);
    installRecord(record);
  }, [installRecord, legacyTransport, ownerKey]);

  const persist = useCallback(
    (record: Omit<StoredLifecycleRecord, 'capabilityMarker' | 'ownerKey'>) => {
      if (!ownerKey) return false;
      try {
        window.sessionStorage.setItem(
          storageKey(ownerKey),
          JSON.stringify({
            ...record,
            capabilityMarker: activeProtocolMarker,
            ownerKey,
            ...(activeTransport ? { transport: activeTransport } : {}),
          }),
        );
        notifyReceiptOwner(ownerKey);
        return true;
      } catch {
        return false;
      }
    },
    [activeProtocolMarker, activeTransport, ownerKey],
  );

  const clearOwnedOperation = useCallback(() => {
    if (ownerKey) {
      removeStoredLifecycleRecord(ownerKey);
    }
    setRejected(null);
    setNeedsReconciliation(null);
    setConfirmedReceipt(null);
    setRestoredPending(null);
    setRestoredReconciliation(null);
    submittingRef.current = null;
    setRestoredSubmitting(null);
  }, [ownerKey]);

  const consumeConfirmedReceipt = useCallback(() => {
    if (
      !ownerKey ||
      confirmedClaims.get(ownerKey) !== receiptClaimToken.current ||
      !removeStoredLifecycleRecord(ownerKey)
    )
      return false;
    confirmedClaims.delete(ownerKey);
    setConfirmedReceipt(null);
    return true;
  }, [ownerKey]);

  const recordConfirmed = useCallback(
    ({
      retainReceipt = false,
      terminalSummary,
    }: {
      retainReceipt?: boolean;
      terminalSummary?: StoredLifecycleRecord['terminalSummary'];
    } = {}) => {
      const command = commandRef.current;
      if (!command || !ownerKey) {
        return false;
      }
      const confirmedRecord = {
        ...command,
        capabilityMarker: activeProtocolMarker,
        operationId: null,
        ownerKey,
        retryAfterSeconds: null,
        status: 'confirmed' as const,
        terminalSummary,
        version: lifecycleRecordVersion,
      };
      if (retainReceipt) {
        const claimedBy = confirmedClaims.get(ownerKey);
        if (claimedBy && claimedBy !== receiptClaimToken.current) return false;
        confirmedClaims.set(ownerKey, receiptClaimToken.current);
        suppressConfirmedSyncRef.current = true;
        const recorded = persist(confirmedRecord);
        suppressConfirmedSyncRef.current = false;
        if (!recorded) {
          confirmedClaims.delete(ownerKey);
          return false;
        }
        if (!subscribedRef.current) {
          confirmedClaims.delete(ownerKey);
          notifyReceiptOwner(ownerKey);
          return false;
        }
        installRecord(confirmedRecord);
        return true;
      }
      suppressConfirmedSyncRef.current = true;
      const recorded = persist(confirmedRecord);
      suppressConfirmedSyncRef.current = false;
      if (!recorded) {
        clearOwnedOperation();
        return subscribedRef.current;
      }
      installRecord(null);
      if (confirmedClaims.has(ownerKey)) return false;
      if (!subscribedRef.current) return false;
      if (!removeStoredLifecycleRecord(ownerKey, false)) {
        notifyReceiptOwner(ownerKey);
        return false;
      }
      return true;
    },
    [
      activeProtocolMarker,
      clearOwnedOperation,
      installRecord,
      ownerKey,
      persist,
      lifecycleRecordVersion,
    ],
  );

  const read = useCallback(async () => {
    const ownerGeneration = ownerIdentity.current.generation;
    setIsReading(true);
    try {
      const response = await readAppointmentRef.current();
      if (ownerIdentity.current.generation !== ownerGeneration) {
        return { kind: 'review' as const, preimage: null };
      }
      const authoritative = appointmentPreimage(response);
      if (!authoritative) {
        setReviewUnavailable(true);
        return { kind: 'review' as const, preimage: null };
      }
      setReviewUnavailable(false);
      setUpgradeRequired(false);
      const current = allowReviewedPreimage
        ? (reviewed ?? displayed)
        : displayed;
      if (!preimageMatches(authoritative, current)) {
        setReviewed(authoritative);
        return { kind: 'review' as const, preimage: authoritative };
      }
      return { kind: 'ready' as const, preimage: authoritative };
    } catch (cause) {
      if (ownerIdentity.current.generation !== ownerGeneration) {
        return { kind: 'review' as const, preimage: null };
      }
      if (appointmentUpgradeGuardSpent(cause)) {
        setUpgradeRequired(true);
        return { kind: 'upgrade' as const, preimage: null };
      }
      setReviewUnavailable(true);
      return { kind: 'review' as const, preimage: null };
    } finally {
      if (ownerIdentity.current.generation === ownerGeneration) {
        setIsReading(false);
      }
    }
  }, [activeCapability, allowReviewedPreimage, displayed, reviewed]);

  const rereadAfterRejection = useCallback(
    async (command: RejectedCommand) => {
      const ownerGeneration = ownerIdentity.current.generation;
      const rejectedCommand = {
        ...command,
        submittedAt: command.submittedAt ?? submittingRef.current?.submittedAt,
      };
      setRejected(rejectedCommand);
      setRestoredPending(null);
      submittingRef.current = null;
      setRestoredSubmitting(null);
      persist({
        ...rejectedCommand,
        retryAfterSeconds: null,
        status: 'rejected',
        version: lifecycleRecordVersion,
      });
      setIsReading(true);
      try {
        const response = await readAppointmentRef.current();
        if (ownerIdentity.current.generation !== ownerGeneration) {
          return null;
        }
        const authoritative = appointmentPreimage(response);
        if (authoritative) {
          setReviewUnavailable(false);
          setReviewed(authoritative);
          return authoritative;
        }
        setReviewUnavailable(true);
        return null;
      } catch (cause) {
        if (ownerIdentity.current.generation !== ownerGeneration) {
          return null;
        }
        if (appointmentUpgradeGuardSpent(cause)) setUpgradeRequired(true);
        setReviewUnavailable(true);
        return null;
      } finally {
        if (ownerIdentity.current.generation === ownerGeneration) {
          setIsReading(false);
        }
      }
    },
    [activeCapability, lifecycleRecordVersion, persist],
  );

  const recordPending = useCallback(
    ({
      bytes,
      idempotencyKey,
      operationId,
      retryAfterSeconds,
    }: RejectedCommand & { retryAfterSeconds: number | null }) => {
      const submittedAt = submittingRef.current?.submittedAt;
      setRejected(null);
      setNeedsReconciliation(null);
      setRestoredPending(null);
      submittingRef.current = null;
      setRestoredSubmitting(null);
      persist({
        bytes,
        idempotencyKey,
        operationId,
        retryAfterSeconds,
        status: 'pending',
        submittedAt,
        version: lifecycleRecordVersion,
      });
    },
    [lifecycleRecordVersion, persist],
  );

  const recordReconciliationRequired = useCallback(
    ({
      bytes,
      idempotencyKey,
      operationId,
    }: Omit<RejectedCommand, 'submittedAt'>) => {
      const submittedAt = submittingRef.current?.submittedAt;
      const recorded = persist({
        bytes,
        idempotencyKey,
        operationId,
        retryAfterSeconds: null,
        status: 'reconciliation_required',
        submittedAt,
        version: lifecycleRecordVersion,
      });
      if (!recorded) return false;
      setRejected(null);
      setRestoredPending(null);
      submittingRef.current = null;
      setRestoredSubmitting(null);
      return true;
    },
    [lifecycleRecordVersion, persist],
  );

  const recordNeedsReconciliation = useCallback(
    ({ bytes, idempotencyKey, operationId }: RejectedCommand) => {
      const submittedAt =
        submittingRef.current?.submittedAt ?? commandRef.current?.submittedAt;
      setRejected(null);
      setRestoredPending(null);
      submittingRef.current = null;
      setRestoredSubmitting(null);
      persist({
        bytes,
        idempotencyKey,
        operationId,
        retryAfterSeconds: null,
        status: 'needs_reconciliation',
        submittedAt,
        version: 2,
      });
    },
    [persist],
  );

  const recordSubmitting = useCallback(
    ({ bytes, idempotencyKey }: SubmittingCommand) => {
      const submittedAt = new Date().toISOString();
      const command = {
        bytes,
        capabilityMarker: activeProtocolMarker,
        idempotencyKey,
        submittedAt,
      };
      const recorded = persist({
        bytes,
        idempotencyKey,
        operationId: null,
        retryAfterSeconds: null,
        status: 'submitting',
        submittedAt,
        version: lifecycleRecordVersion,
      });
      if (!recorded) return false;
      setRejected(null);
      setNeedsReconciliation(null);
      setRestoredPending(null);
      commandRef.current = command;
      submittingRef.current = command;
      setRestoredSubmitting(command);
      return true;
    },
    [activeProtocolMarker, lifecycleRecordVersion, persist],
  );

  const idempotencyKeyFor = (bytes: string, initialKey?: string) => {
    if (commandRef.current?.bytes === bytes) {
      return commandRef.current.idempotencyKey;
    }
    const idempotencyKey =
      commandRef.current === null && initialKey
        ? initialKey
        : window.crypto.randomUUID();
    commandRef.current = { bytes, idempotencyKey };
    return idempotencyKey;
  };
  const submissionFor = (bytes: string, retryRestoredSubmitting = false) => {
    const submitting = submittingRef.current;
    if (submitting) {
      if (submitting.bytes !== bytes) {
        return { kind: 'review' as const, reason: 'changed-command' as const };
      }
      if (!retryRestoredSubmitting) {
        return {
          kind: 'review' as const,
          reason: 'ambiguous-outcome' as const,
        };
      }
      return {
        idempotencyKey: submitting.idempotencyKey,
        kind: 'ready' as const,
      };
    }
    return { idempotencyKey: idempotencyKeyFor(bytes), kind: 'ready' as const };
  };
  const currentCommand = useCallback(() => commandRef.current, []);
  const currentSubmissionStartedAt = useCallback(
    () => submittingRef.current?.submittedAt ?? null,
    [],
  );

  return {
    clearOwnedOperation,
    confirmedReceipt:
      confirmedReceipt?.ownerKey === ownerKey ? confirmedReceipt : null,
    consumeConfirmedReceipt,
    currentCommand,
    currentSubmissionStartedAt,
    idempotencyKeyFor,
    isReading,
    needsReconciliation,
    read,
    recordConfirmed,
    recordNeedsReconciliation,
    recordPending,
    recordReconciliationRequired,
    recordSubmitting,
    rejected,
    rereadAfterRejection,
    restoredPending,
    restoredReconciliation,
    restoredSubmitting,
    reviewUnavailable,
    reviewed,
    submissionBlocked: (bytes: string) => rejected?.bytes === bytes,
    submissionFor,
    upgradeRequired,
  };
};
